'use client';

import { useCallback } from 'react';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { useTranslation } from 'next-i18next';
import { Modal } from '@components/common/Modal/Modal';
import { useReduxUser } from '@hooks/common/useAuthClient/useReduxUser';
import GeminiVoiceWidget from '@components/features/voice/GeminiVoiceWidget';
import { fetchBestieClientVoiceSession } from '@services/voice/voiceSessionApi';

interface EmployerVoiceModalProps {
  locale: Locale;
}

/**
 * Employer voice agent (the `bestie-client` agent) — opened from /wallet/services.
 *
 * The session endpoint requires full authentication, so the caller gates on auth before
 * showing this modal and the not-logged greeting variant is unreachable here.
 * The session carries no prompt: the agent definition supplies its own identity,
 * knowledge and tools.
 */
export const EmployerVoiceModal = NiceModal.create(({ locale }: EmployerVoiceModalProps) => {
  const modal = useModal();
  const { t } = useTranslation('common');
  const { headerInfo } = useReduxUser();

  // `name` is the wallet/billing name — the *company* on an employer account. The person's
  // own name is `candidateName` (HeaderInfoBuilder sets it from $user->getName() for both
  // roles), which is what the greeting should use. Falls back to empty rather than to the
  // company name, so a missing name just drops out of the sentence.
  const firstName = headerInfo?.candidateName?.trim().split(' ')[0] ?? '';
  const firstMessage = t('inbound.greeting.employer.logged', { first_name: firstName });

  const closeModal = useCallback(() => {
    void modal.hide();
    modal.remove();
  }, [modal]);

  // Locale is threaded through so the session request carries the right Accept-Language;
  // defaulting it would send `ro` for users on /en/*.
  const fetchSession = useCallback(() => fetchBestieClientVoiceSession(locale), [locale]);

  return (
    <Modal
      isOpen={modal.visible}
      onClose={closeModal}
      backdropClickDisabled
      size="sm"
      className="!max-w-sm !rounded-3xl"
      body={<GeminiVoiceWidget firstMessage={firstMessage} fetchSession={fetchSession} onClose={closeModal} />}
    />
  );
});
